// src/pages/Community/JoinTroupePage.tsx
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import { StudioErrorState } from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  EYEBROW,
  humanise,
  INPUT,
  LABEL,
  PANEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useSEO from '../../hooks/useSEO';
import communityService from '../../services/communityService';
import { StudioApiError, toDisplayMessage } from '../../services/studioApi';
import { AVAILABILITIES, CRAFTS } from '../../types/studio';
import type { Availability, Craft, MemberProfile } from '../../types/studio';

const MAX_SECONDARY_CRAFTS = 4;
const MAX_SKILLS = 20;
const BIO_LIMIT = 4000;

interface FormState {
  displayName: string;
  headline: string;
  bio: string;
  location: string;
  avatarUrl: string;
  showreelUrl: string;
  primaryCraft: Craft;
  secondaryCrafts: Craft[];
  skills: string[];
  availability: Availability;
  instagramUrl: string;
  websiteUrl: string;
  imdbUrl: string;
}

const EMPTY_FORM: FormState = {
  displayName: '',
  headline: '',
  bio: '',
  location: '',
  avatarUrl: '',
  showreelUrl: '',
  primaryCraft: 'ACTOR',
  secondaryCrafts: [],
  skills: [],
  availability: 'OPEN_TO_WORK',
  instagramUrl: '',
  websiteUrl: '',
  imdbUrl: '',
};

/** The server returns secondary crafts as labels; map them back to enum values. */
function craftFromLabel(label: string): Craft | null {
  const normalized = label.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return (CRAFTS as readonly string[]).includes(normalized) ? (normalized as Craft) : null;
}

function toFormState(profile: MemberProfile): FormState {
  return {
    displayName: profile.displayName,
    headline: profile.headline ?? '',
    bio: profile.bio ?? '',
    location: profile.location ?? '',
    avatarUrl: profile.avatarUrl ?? '',
    showreelUrl: profile.showreelUrl ?? '',
    primaryCraft: profile.primaryCraft,
    secondaryCrafts: profile.secondaryCrafts
      .map(craftFromLabel)
      .filter((craft): craft is Craft => craft !== null),
    skills: profile.skills,
    availability: profile.availability,
    instagramUrl: profile.instagramUrl ?? '',
    websiteUrl: profile.websiteUrl ?? '',
    imdbUrl: profile.imdbUrl ?? '',
  };
}

const JoinTroupePage = () => {
  useSEO({
    title: 'Join the Troupe — StrymHub Community',
    description:
      'Create your StrymHub member profile. Actors, directors, writers, camera, sound, edit, music and design — get your own page and get cast in our productions.',
    keywords:
      'join strymhub, become a member, sheffield casting, uk crew signup, actor profile, film community signup',
    type: 'website',
  });

  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [existing, setExisting] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [skillInput, setSkillInput] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setLoadError(null);

    communityService
      .getMyProfile(controller.signal)
      .then((profile) => {
        if (!active) return;
        setExisting(profile);
        setForm(toFormState(profile));
      })
      .catch((error: unknown) => {
        if (!active || controller.signal.aborted) return;
        // 404 simply means "not a member yet" — that's the create path, not an error.
        if (error instanceof StudioApiError && error.isNotFound) {
          setExisting(null);
          return;
        }
        if (error instanceof StudioApiError && error.isUnauthenticated) {
          navigate('/login?redirect=/community/join', { replace: true });
          return;
        }
        setLoadError(toDisplayMessage(error, 'We could not load your profile.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [navigate, reloadToken]);

  const isEditing = existing !== null;

  const craftOptions = useMemo(
    () => CRAFTS.map((craft) => ({ value: craft, label: humanise(craft) })),
    [],
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleSecondaryCraft = (craft: Craft) => {
    setForm((current) => {
      if (current.secondaryCrafts.includes(craft)) {
        return {
          ...current,
          secondaryCrafts: current.secondaryCrafts.filter((value) => value !== craft),
        };
      }
      if (current.secondaryCrafts.length >= MAX_SECONDARY_CRAFTS) return current;
      return { ...current, secondaryCrafts: [...current.secondaryCrafts, craft] };
    });
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (!skill) return;
    setForm((current) => {
      if (current.skills.length >= MAX_SKILLS || current.skills.includes(skill)) return current;
      return { ...current, skills: [...current.skills, skill] };
    });
    setSkillInput('');
  };

  const handleSkillKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addSkill();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    setSuccess(null);

    if (!form.displayName.trim()) {
      setSubmitError('Tell us the name you want to be credited under.');
      return;
    }

    setSubmitting(true);
    const payload = {
      displayName: form.displayName.trim(),
      headline: form.headline.trim() || undefined,
      bio: form.bio.trim() || undefined,
      location: form.location.trim() || undefined,
      avatarUrl: form.avatarUrl.trim() || undefined,
      showreelUrl: form.showreelUrl.trim() || undefined,
      primaryCraft: form.primaryCraft,
      secondaryCrafts: form.secondaryCrafts,
      skills: form.skills,
      availability: form.availability,
      instagramUrl: form.instagramUrl.trim() || undefined,
      websiteUrl: form.websiteUrl.trim() || undefined,
      imdbUrl: form.imdbUrl.trim() || undefined,
    };

    try {
      const saved = isEditing
        ? await communityService.updateMyProfile(payload)
        : await communityService.join(payload);

      setExisting(saved);
      setForm(toFormState(saved));
      setSuccess(
        isEditing
          ? 'Profile updated.'
          : "You're in. The studio reviews new members before they go live in the troupe — we'll email you.",
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error instanceof StudioApiError && error.isUnauthenticated) {
        navigate('/login?redirect=/community/join');
        return;
      }
      setSubmitError(toDisplayMessage(error, 'We could not save your profile.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`} role="status" aria-label="Loading">
          <Loader2 className="h-6 w-6 animate-spin text-orange-400" aria-hidden="true" />
          <span className="sr-only">Loading your profile…</span>
        </div>
      </StudioShell>
    );
  }

  if (loadError) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`}>
          <StudioErrorState
            message={loadError}
            onRetry={() => setReloadToken((token) => token + 1)}
          />
        </div>
      </StudioShell>
    );
  }

  return (
    <StudioShell>
      <StudioHero
        eyebrow="The StrymHub Community"
        title={isEditing ? 'Your member' : 'Join the'}
        accent={isEditing ? 'profile.' : 'troupe.'}
        lede={
          isEditing
            ? 'This is what the studio sees when we cast, and what the world sees on your public page.'
            : "Tell us what you do and what you're good at. There's no fee, no audition tape required to join, and no gatekeeper — this is how you get into the pool we cast from."
        }
      />

      <div className={`${CONTAINER} py-12`}>
        <div className="grid gap-12 lg:grid-cols-[1fr_minmax(0,300px)]">
          <form onSubmit={handleSubmit} className="max-w-2xl space-y-10">
            {success && (
              <div
                role="status"
                className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div>
                  <p>{success}</p>
                  {existing && (
                    <Link
                      to={`/community/troupe/${existing.slug}`}
                      className="mt-2 inline-block font-semibold underline underline-offset-4"
                    >
                      View your page
                    </Link>
                  )}
                </div>
              </div>
            )}

            {submitError && (
              <div
                role="alert"
                className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>{submitError}</p>
              </div>
            )}

            {/* Identity */}
            <fieldset className="space-y-5">
              <legend className={EYEBROW}>How you&apos;re credited</legend>

              <div>
                <label htmlFor="displayName" className={LABEL}>
                  Name you want credited <span className="text-orange-400">*</span>
                </label>
                <input
                  id="displayName"
                  required
                  maxLength={120}
                  value={form.displayName}
                  onChange={(event) => update('displayName', event.target.value)}
                  className={INPUT}
                  placeholder="e.g. Maya Osei"
                />
              </div>

              <div>
                <label htmlFor="headline" className={LABEL}>
                  One line about you
                </label>
                <input
                  id="headline"
                  maxLength={160}
                  value={form.headline}
                  onChange={(event) => update('headline', event.target.value)}
                  className={INPUT}
                  placeholder="Stage actor moving into screen. Sheffield-based."
                />
                <p className="mt-1.5 text-xs text-neutral-500">
                  {form.headline.length}/160 — this is what shows on your card in the troupe.
                </p>
              </div>

              <div>
                <label htmlFor="location" className={LABEL}>
                  Where you&apos;re based
                </label>
                <input
                  id="location"
                  maxLength={120}
                  value={form.location}
                  onChange={(event) => update('location', event.target.value)}
                  className={INPUT}
                  placeholder="Sheffield, UK"
                />
              </div>

              <div>
                <label htmlFor="avatarUrl" className={LABEL}>
                  Headshot URL
                </label>
                <input
                  id="avatarUrl"
                  type="url"
                  value={form.avatarUrl}
                  onChange={(event) => update('avatarUrl', event.target.value)}
                  className={INPUT}
                  placeholder="https://…"
                />
              </div>
            </fieldset>

            {/* Craft */}
            <fieldset className="space-y-5">
              <legend className={EYEBROW}>What you do</legend>

              <div>
                <label htmlFor="primaryCraft" className={LABEL}>
                  Primary craft <span className="text-orange-400">*</span>
                </label>
                <select
                  id="primaryCraft"
                  value={form.primaryCraft}
                  onChange={(event) => update('primaryCraft', event.target.value as Craft)}
                  className={INPUT}
                >
                  {craftOptions.map((option) => (
                    <option key={option.value} value={option.value} className="bg-neutral-900">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className={LABEL} id="secondary-crafts-label">
                  Anything else you do (up to {MAX_SECONDARY_CRAFTS})
                </p>
                <div
                  role="group"
                  aria-labelledby="secondary-crafts-label"
                  className="flex flex-wrap gap-2"
                >
                  {craftOptions
                    .filter((option) => option.value !== form.primaryCraft)
                    .map((option) => {
                      const selected = form.secondaryCrafts.includes(option.value);
                      const disabled =
                        !selected && form.secondaryCrafts.length >= MAX_SECONDARY_CRAFTS;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={selected}
                          disabled={disabled}
                          onClick={() => toggleSecondaryCraft(option.value)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 ${
                            selected
                              ? 'border-orange-500/50 bg-orange-500/15 text-orange-200'
                              : 'border-white/10 text-neutral-400 hover:border-white/25 hover:text-neutral-200'
                          } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                </div>
              </div>

              <div>
                <label htmlFor="skills" className={LABEL}>
                  Specific skills
                </label>
                <div className="flex gap-2">
                  <input
                    id="skills"
                    value={skillInput}
                    onChange={(event) => setSkillInput(event.target.value)}
                    onKeyDown={handleSkillKeyDown}
                    className={INPUT}
                    placeholder="Stage combat, RP accent, Steadicam… press Enter"
                  />
                  <button type="button" onClick={addSkill} className={SECONDARY_BUTTON}>
                    Add
                  </button>
                </div>
                {form.skills.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {form.skills.map((skill) => (
                      <li key={skill}>
                        <button
                          type="button"
                          onClick={() =>
                            update(
                              'skills',
                              form.skills.filter((value) => value !== skill),
                            )
                          }
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-300 transition-colors hover:border-red-500/40 hover:text-red-300"
                        >
                          {skill}
                          <X className="h-3 w-3" aria-hidden="true" />
                          <span className="sr-only">Remove {skill}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label htmlFor="availability" className={LABEL}>
                  Availability
                </label>
                <select
                  id="availability"
                  value={form.availability}
                  onChange={(event) => update('availability', event.target.value as Availability)}
                  className={INPUT}
                >
                  {AVAILABILITIES.map((option) => (
                    <option key={option} value={option} className="bg-neutral-900">
                      {humanise(option)}
                    </option>
                  ))}
                </select>
              </div>
            </fieldset>

            {/* Your work */}
            <fieldset className="space-y-5">
              <legend className={EYEBROW}>Your work</legend>

              <div>
                <label htmlFor="bio" className={LABEL}>
                  Your story
                </label>
                <textarea
                  id="bio"
                  rows={7}
                  maxLength={BIO_LIMIT}
                  value={form.bio}
                  onChange={(event) => update('bio', event.target.value)}
                  className={INPUT}
                  placeholder="Where you trained, what you've done, what you want to make. Write it like you'd say it."
                />
                <p className="mt-1.5 text-xs text-neutral-500">
                  {form.bio.length}/{BIO_LIMIT}
                </p>
              </div>

              <div>
                <label htmlFor="showreelUrl" className={LABEL}>
                  Showreel URL
                </label>
                <input
                  id="showreelUrl"
                  type="url"
                  value={form.showreelUrl}
                  onChange={(event) => update('showreelUrl', event.target.value)}
                  className={INPUT}
                  placeholder="https://vimeo.com/…"
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-3">
                <div>
                  <label htmlFor="websiteUrl" className={LABEL}>
                    Website
                  </label>
                  <input
                    id="websiteUrl"
                    type="url"
                    value={form.websiteUrl}
                    onChange={(event) => update('websiteUrl', event.target.value)}
                    className={INPUT}
                  />
                </div>
                <div>
                  <label htmlFor="instagramUrl" className={LABEL}>
                    Instagram
                  </label>
                  <input
                    id="instagramUrl"
                    type="url"
                    value={form.instagramUrl}
                    onChange={(event) => update('instagramUrl', event.target.value)}
                    className={INPUT}
                  />
                </div>
                <div>
                  <label htmlFor="imdbUrl" className={LABEL}>
                    IMDb
                  </label>
                  <input
                    id="imdbUrl"
                    type="url"
                    value={form.imdbUrl}
                    onChange={(event) => update('imdbUrl', event.target.value)}
                    className={INPUT}
                  />
                </div>
              </div>
            </fieldset>

            <div className="flex flex-wrap items-center gap-4 border-t border-white/10 pt-8">
              <button type="submit" disabled={submitting} className={PRIMARY_BUTTON}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {isEditing ? 'Save changes' : 'Join the troupe'}
              </button>
              {isEditing && existing && (
                <Link to={`/community/troupe/${existing.slug}`} className={SECONDARY_BUTTON}>
                  View public page
                </Link>
              )}
            </div>
          </form>

          <aside className="space-y-6">
            <div className={`${PANEL} p-6`}>
              <h2 className={EYEBROW}>What happens next</h2>
              <ol className="mt-4 space-y-4 text-sm leading-relaxed text-neutral-400">
                <li>
                  <span className="font-semibold text-white">1. You submit.</span> Takes five
                  minutes. Nothing here is permanent — you can edit any of it later.
                </li>
                <li>
                  <span className="font-semibold text-white">2. We read it.</span> A person, not an
                  algorithm. We&apos;re looking for people we want to work with, not headshots.
                </li>
                <li>
                  <span className="font-semibold text-white">3. You go live.</span> Your page joins
                  the public troupe and you enter the pool we cast every production from.
                </li>
              </ol>
            </div>

            <div className={`${PANEL} p-6`}>
              <h2 className={EYEBROW}>Straight answers</h2>
              <dl className="mt-4 space-y-4 text-sm text-neutral-400">
                <div>
                  <dt className="font-semibold text-white">Does it cost anything?</dt>
                  <dd className="mt-1">No. Not to join, not to apply, not ever.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-white">Do I need experience?</dt>
                  <dd className="mt-1">
                    No. We scout from theatre groups, universities and open calls specifically
                    because people can&apos;t get on screen without already being on screen.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-white">Do I have to be in Sheffield?</dt>
                  <dd className="mt-1">
                    It helps for on-set roles. Writing, edit, sound, music and animation work
                    remotely.
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </StudioShell>
  );
};

export default JoinTroupePage;
